import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Navigation } from "lucide-react";

// Paradise Studios — 2587–2615 Gibbston Valley Highway, Gibbston
const ADDRESS = "2587-2615 Gibbston Valley Highway, Gibbston, Central Otago, New Zealand";
const ENCODED = encodeURIComponent(ADDRESS);
const EMBED_SRC = `https://www.google.com/maps?q=${ENCODED}&t=k&z=15&output=embed`;
const EXTERNAL = `https://www.google.com/maps/search/?api=1&query=${ENCODED}`;

export default function Map() {
  return (
    <section
      id="map"
      data-testid="map-section"
      className="relative py-28 md:py-36 border-t border-white/5"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#E2B464]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E2B464]">
                Find Us · Victoria Flats
              </span>
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[0.95] tracking-tighter">
              The wet-weather cover,{" "}
              <span className="italic text-[#E2B464]">on the map.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 self-end space-y-3 text-zinc-400 leading-relaxed">
            <p>
              Paradise Studios is located at 2587–2615 Gibbston Valley Highway
              in the heart of the Gibbston wine region, approximately 28 km
              (around a 25–30 minute drive) from Queenstown Airport and well
              within the 40 km production radius.
            </p>
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 pt-2">
              <Navigation className="w-3 h-3 text-[#E2B464]" />
              <span>2587–2615 Gibbston Valley Highway</span>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.8 }}
          className="relative border border-white/10 overflow-hidden"
        >
          <div className="aspect-[16/9] md:aspect-[21/9] relative bg-[#0a0a0a]">
            <iframe
              data-testid="map-iframe"
              src={EMBED_SRC}
              title="Paradise Studios — Victoria Flats location"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full grayscale-[0.2]"
              style={{ border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.65) brightness(0.95) contrast(0.95)" }}
              allowFullScreen
            />
            {/* Pin marker overlay (visual emphasis on top of the map) */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-[#E2B464]/40 animate-ping" />
                <div className="relative w-12 h-12 rounded-full bg-[#E2B464] flex items-center justify-center shadow-[0_0_30px_rgba(226,180,100,0.6)]">
                  <MapPin className="w-5 h-5 text-black" strokeWidth={2.5} />
                </div>
              </div>
            </div>

            {/* Bottom info chip */}
            <div className="absolute bottom-5 left-5 right-5 md:left-8 md:right-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pointer-events-none">
              <div className="bg-[#050505]/85 backdrop-blur border border-white/10 px-5 py-4 max-w-md pointer-events-auto">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E2B464] mb-1">
                  Paradise Studios · Wet-Weather Cover
                </p>
                <p className="font-display text-lg text-white tracking-tight">
                  2587–2615 Gibbston Valley Highway
                </p>
                <p className="text-xs text-zinc-400 mt-1">Gibbston · Central Otago, NZ</p>
              </div>
              <a
                href={EXTERNAL}
                target="_blank"
                rel="noreferrer"
                data-testid="map-open-button"
                className="btn-primary px-5 py-3 text-sm font-medium inline-flex items-center gap-2 pointer-events-auto self-start sm:self-auto"
              >
                Open in Maps <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
